import { ref } from 'vue';
import type { BudgetGroupAthleteItem, BudgetGroupExpanded } from '@/types/budgetGroups';

type ViewKeys = 'INSTITUTIONAL' | 'THIRD_PARTY' | 'TOTAL_EARNINGS' | 'SCHOLARSHIPS' | 'DEPTH_CHART';

/**
 * "All Years" is an option in the same budget-period multi-select as the academic years (empty
 * selection = show all). It is not a real budget period, so row matching ignores it.
 *
 * Deliberately its own literal rather than an alias of the grid column id — the filter value is
 * persisted in session state and must not shift if the column id is ever renamed.
 */
export const ALL_YEARS_FILTER_ID = 'allYears';

/**
 * Drop the `ALL_YEARS_FILTER_ID` sentinel from a budget-period selection, leaving only real
 * budget TWRNs. The token is a column-visibility option, not a real period, so anything doing
 * row matching or per-year filtering must strip it first. Kept as one helper so the table,
 * overview, and export paths cannot drift apart.
 */
export function stripAllYearsFilterId(selectedBudgetPeriodIds: string[]): string[] {
  return selectedBudgetPeriodIds.filter((id) => id !== ALL_YEARS_FILTER_ID);
}

const positionFilters = ref<Record<string, string[]>>({});
const budgetPeriodFilters = ref<Record<string, string[]>>({});
const currentView = ref<ViewKeys | null>(null);

/**
 * Whether the athlete has roster data for at least one budget whose `academicYearDateId` is in
 * `selectedBudgetPeriodIds`. These are the same string values stored by `useRosterFilter` and used
 * in `RosterFilter` checkbox values (`academicYearDateId`), not display labels.
 *
 * The `ALL_YEARS_FILTER_ID` token is ignored — selecting "All Years" means all years, so it does
 * not restrict which athletes are shown.
 */
export function athleteMatchesRosterBudgetPeriodFilter(
  athlete: BudgetGroupAthleteItem | undefined,
  selectedBudgetPeriodIds: string[],
  budgetGroup: BudgetGroupExpanded | null,
): boolean {
  const periodIds = stripAllYearsFilterId(selectedBudgetPeriodIds);
  if (periodIds.length === 0) return true;
  if (!athlete || !budgetGroup) return false;
  return budgetGroup.budgets.some(
    (b) =>
      periodIds.includes(b.twrn)
      && !!athlete.budgets[b.twrn],
  );
}

export function useRosterFilter() {
  const setCurrentView = (view: ViewKeys | null) => {
    currentView.value = view;
    if (view && !positionFilters.value[view]) {
      positionFilters.value[view] = [];
      budgetPeriodFilters.value[view] = [];
    }
  };

  const setPositionFilter = (filter: string[]) => {
    if (!currentView.value) return;
    positionFilters.value[currentView.value] = filter;
  };

  const getPositionFilter = () => {
    return currentView.value ? positionFilters.value[currentView.value] || [] : [];
  };

  const setBudgetPeriodFilter = (filter: string[]) => {
    if (!currentView.value) return;
    budgetPeriodFilters.value[currentView.value] = filter;
  };

  const getBudgetPeriodFilter = () => {
    return currentView.value ? budgetPeriodFilters.value[currentView.value] || [] : [];
  };

  /**
   * Whether the "All Years" column should be shown. Mirrors the academic-year selection:
   * an empty selection means "show all", so All Years is visible; otherwise it is only
   * visible when the `ALL_YEARS_FILTER_ID` option is explicitly selected.
   */
  const shouldShowAllYearsColumn = () => {
    const periods = getBudgetPeriodFilter();
    return periods.length === 0 || periods.includes(ALL_YEARS_FILTER_ID);
  };

  /**
   * The active budget-period filter with the `ALL_YEARS_FILTER_ID` sentinel removed — the real
   * academic-year TWRNs the selection restricts rows/columns to. Shared by the tables and the
   * overview so both interpret the same selection identically.
   */
  const getActiveBudgetPeriodIds = () => stripAllYearsFilterId(getBudgetPeriodFilter());

  /**
   * Whether a budget TWRN should be included under the active budget-period selection.
   * An empty selection means "show every year". A non-empty selection that contains no real TWRN
   * (i.e. only the `ALL_YEARS_FILTER_ID` sentinel) means "no years" — this matches the table, where
   * selecting only "All Years" hides the academic-year columns instead of showing them all.
   * Centralised so the table, overview, and export agree on the sentinel's meaning.
   */
  const budgetPeriodSelectionIncludes = (budgetTwrn: string) => {
    const periods = getBudgetPeriodFilter();
    if (periods.length === 0) return true;
    return getActiveBudgetPeriodIds().includes(budgetTwrn);
  };

  const getFilterCount = () => {
    if (!currentView.value) return 0;
    const positions = positionFilters.value[currentView.value] || [];
    const periods = budgetPeriodFilters.value[currentView.value] || [];
    return positions.length + periods.length;
  };

  const resetFilters = () => {
    positionFilters.value = {};
    budgetPeriodFilters.value = {};
  };

  return {
    currentView,
    positionFilters,
    budgetPeriodFilters,
    setCurrentView,
    setPositionFilter,
    getPositionFilter,
    setBudgetPeriodFilter,
    getBudgetPeriodFilter,
    getActiveBudgetPeriodIds,
    budgetPeriodSelectionIncludes,
    shouldShowAllYearsColumn,
    getFilterCount,
    resetFilters,
  };
}
